import React, { useState, useMemo, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Search, 
  Menu, 
  X, 
  UtilityPole, 
  Settings,
  Github,
  Star as StarIcon,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  History as HistoryIcon,
  Download,
  CheckCircle2,
  Share2,
  ShieldCheck,
  LayoutGrid,
  User as UserIcon,
  Trash2
} from 'lucide-react';
import { cn } from './lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './components/ui/tooltip';
import { TOOLS, CATEGORIES } from './constants';
import Dashboard from './components/Dashboard';
import UserPanel from './components/UserPanel';
import UserSidePanel from './components/UserSidePanel';
import ToolLayout from './components/ToolLayout';
import AdminPanel from './components/AdminPanel';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import About from './components/legal/About';
import NotFound from './components/NotFound';
import Contact from './components/Contact';
import CookieBanner from './components/CookieBanner';
import Unsubscribe from './components/Unsubscribe';
import { auth, authService, usersService } from './lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
const WordCounter = React.lazy(() => import('./components/tools/WordCounter'));
const CaseConverter = React.lazy(() => import('./components/tools/CaseConverter'));
const SmartRewriter = React.lazy(() => import('./components/tools/SmartRewriter'));
const JsonFormatter = React.lazy(() => import('./components/tools/JsonFormatter'));
const Base64Converter = React.lazy(() => import('./components/tools/Base64Converter'));
const HashGenerator = React.lazy(() => import('./components/tools/HashGenerator'));
const PasswordGenerator = React.lazy(() => import('./components/tools/PasswordGenerator'));
const LoremIpsum = React.lazy(() => import('./components/tools/LoremIpsum'));
const UnitConverter = React.lazy(() => import('./components/tools/UnitConverter'));
const ColorPicker = React.lazy(() => import('./components/tools/ColorPicker'));

const QrGenerator = React.lazy(() => import('./components/tools/QrGenerator'));
const UnixTimestamp = React.lazy(() => import('./components/tools/UnixTimestamp'));
const Minifier = React.lazy(() => import('./components/tools/Minifier'));
const PxToRem = React.lazy(() => import('./components/tools/PxToRem'));
const DuplicateRemover = React.lazy(() => import('./components/tools/DuplicateRemover'));
const UrlEncoder = React.lazy(() => import('./components/tools/UrlEncoder'));
const TextToSlug = React.lazy(() => import('./components/tools/TextToSlug'));
const GradientGenerator = React.lazy(() => import('./components/tools/GradientGenerator'));

// Direct tool mapping
const TOOL_COMPONENTS: Record<string, React.ReactNode> = {
  'word-counter': <WordCounter />,
  'case-converter': <CaseConverter />,
  'ai-rewriter': <SmartRewriter />,
  'json-formatter': <JsonFormatter />,
  'base64-converter': <Base64Converter />,
  'hash-generator': <HashGenerator />,
  'password-gen': <PasswordGenerator />,
  'lorem-ipsum': <LoremIpsum />,
  'unit-converter': <UnitConverter />,
  'color-picker': <ColorPicker />,
  'qr-generator': <QrGenerator />,
  'unix-timestamp': <UnixTimestamp />,
  'minifier': <Minifier />,
  'px-to-rem': <PxToRem />,
  'duplicate-remover': <DuplicateRemover />,
  'url-encoder': <UrlEncoder />,
  'text-to-slug': <TextToSlug />,
  'gradient-generator': <GradientGenerator />,
};

// Direct tool imports (placeholder components for others)
const PlaceholderTool = ({ name }: { name: string }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-12 bg-white/40 dark:bg-slate-900/40 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
    <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-3xl flex items-center justify-center mb-6 shadow-inner">
      <Settings className="w-10 h-10 text-slate-200 dark:text-slate-700 animate-spin-slow" />
    </div>
    <h2 className="text-2xl font-bold tracking-tight mb-2 underline dark:text-white uppercase-none font-sans">{name}</h2>
    <p className="text-slate-400 dark:text-slate-500 text-sm max-w-xs font-medium uppercase-none">This high-performance utility is currently being engineered. Check back soon.</p>
  </div>
);

const SidebarTooltip = ({ children, tooltip, isCollapsed }: { children: React.ReactNode, tooltip: string, isCollapsed: boolean }) => {
  if (!isCollapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={16}>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export default function App() {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      return saved === 'dark';
    }
    return false;
  });

  const [favorites, setFavorites] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('favorites');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  const [history, setHistory] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tool-history');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'info' } | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        usersService.saveUser(user);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    localStorage.setItem('tool-history', JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const toggleFavorite = (id: string) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const addToHistory = (id: string) => {
    setHistory(prev => {
      const filtered = prev.filter(item => item !== id);
      return [id, ...filtered].slice(0, 10);
    });
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'QuicklyUtils',
        text: 'Check out this awesome tool on QuicklyUtils!',
        url: url,
      }).catch(() => {
        navigator.clipboard.writeText(url);
        setToast({ message: 'Link copied to clipboard!', type: 'success' });
      });
    } else {
      navigator.clipboard.writeText(url);
      setToast({ message: 'Link copied to clipboard!', type: 'success' });
    }
  };

  const [isUserPanelOpen, setIsUserPanelOpen] = useState(false);

  return (
    <BrowserRouter>
      <Layout 
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        favorites={favorites}
        setFavorites={setFavorites}
        toggleFavorite={toggleFavorite}
        history={history}
        setHistory={setHistory}
        addToHistory={addToHistory}
        handleShare={handleShare}
        toast={toast}
        setToast={setToast}
        currentUser={currentUser}
        isUserPanelOpen={isUserPanelOpen}
        setIsUserPanelOpen={setIsUserPanelOpen}
      />
    </BrowserRouter>
  );
}

function Layout({ darkMode, setDarkMode, favorites, toggleFavorite, setFavorites, history, setHistory, addToHistory, handleShare, toast, setToast, currentUser, isUserPanelOpen, setIsUserPanelOpen }: any) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; subMessage?: string; onConfirm: () => void } | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const location = useLocation();

  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window !== 'undefined') {
      const isPwa = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;
      return isPwa;
    }
    return false;
  });

  useEffect(() => {
    if (showSplash) {
      const timer = setTimeout(() => {
        setShowSplash(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showSplash]);

  const isAdmin = currentUser?.email === 'allysonbelo@gmail.com';

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  useEffect(() => {
    if (isMenuOpen || isSettingsOpen || isHistoryOpen || isUserPanelOpen || confirmDialog != null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, isSettingsOpen, isHistoryOpen, isUserPanelOpen, confirmDialog]);

  useEffect(() => {
    const segments = location.pathname.split('/');
    const toolId = segments[segments.length - 1];
    
    // Tracking page views
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });

    if (toolId && TOOLS.find(t => t.id === toolId)) {
      addToHistory(toolId);
    }
  }, [location.pathname]);

  const isHomeActive = location.pathname === '/' && !isMenuOpen && !isHistoryOpen && !isSettingsOpen;
  const isMyPanelActive = location.pathname === '/my-panel' && !isMenuOpen && !isHistoryOpen && !isSettingsOpen;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-bg dark:bg-bg-dark text-ink dark:text-ink-dark transition-colors duration-300 overflow-x-hidden">
      
      {/* PWA Splash Screen */}
      <AnimatePresence>
        {showSplash && (
          <motion.div 
            key="pwa-splash"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.05, filter: "blur(4px)" }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="fixed inset-0 z-[9999] bg-bg dark:bg-bg-dark flex flex-col items-center justify-center p-6"
          >
            <motion.div
               initial={{ scale: 0.8, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               transition={{ type: 'spring', bounce: 0.5, duration: 1 }}
               className="flex flex-col items-center justify-center text-center max-w-sm w-full"
            >
              <div className="w-24 h-24 bg-accent rounded-[2rem] flex items-center justify-center text-white text-5xl font-bold shadow-2xl shadow-accent/40 mb-6">
                Q
              </div>
              <h1 className="text-4xl font-black text-ink dark:text-ink-dark tracking-tight mb-2">
                QuicklyUtils<span className="text-accent">.</span>
              </h1>
              <p className="text-slate-500 font-medium mb-12 uppercase tracking-widest text-xs">Your all-in-one toolkit</p>
              
              <div className="w-32 h-1 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden relative">
                <motion.div 
                  initial={{ left: "-100%" }}
                  animate={{ left: "100%" }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-y-0 w-1/2 bg-accent rounded-full"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className={cn(
              "fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 border rounded-2xl shadow-2xl flex items-center gap-3",
              toast.type === 'success' ? "bg-slate-900 border-emerald-500/30 text-white" : "bg-slate-900 border-rose-500/30 text-white"
            )}
          >
            {toast.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <X className="w-4 h-4 text-rose-400" />
            )}
            <span className="text-xs font-bold tracking-tight">{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between p-4 border-b border-line dark:border-line-dark bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <Link to="/" className="flex items-center gap-3 font-bold text-xl tracking-tight">
          <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center text-white text-lg">Q</div>
          QuicklyUtils<span className="text-accent">.</span>
        </Link>
        <div className="flex items-center gap-2">
          {deferredPrompt && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button aria-label="Install App" onClick={handleInstallClick} className="p-2 text-accent bg-accent/10 rounded-lg hover:bg-accent/20 transition-colors">
                    <Download className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={8}>Install App</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <button aria-label="Toggle Theme" onClick={() => setDarkMode(!darkMode)} className="p-2 text-slate-500 hover:text-accent transition-colors">
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Sidebar Navigation */}
      <TooltipProvider delayDuration={0}>
        <aside className={cn(
          "fixed inset-y-0 left-0 z-40 transition-all duration-300 bg-white dark:bg-slate-900 border-r border-line dark:border-line-dark shadow-sm flex flex-col md:translate-x-0 h-screen",
          isSidebarCollapsed ? "md:w-20" : "md:w-80",
          isMenuOpen ? "translate-x-0 w-80" : "-translate-x-full"
        )}>
          {/* Collapse Toggle Button (Desktop) - Now outside overflow container */}
        <button 
          aria-label={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className={cn(
            "hidden md:flex absolute -right-4 top-24 w-8 h-8 rounded-full items-center justify-center transition-all z-[60] shadow-xl border-2 hover:scale-110 active:scale-95",
            "bg-accent text-white border-white dark:border-slate-900"
          )}
          title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        <div className={cn(
          "h-full flex flex-col overflow-hidden transition-all duration-300 pb-[100px] md:pb-6",
          isSidebarCollapsed ? "p-2 md:pb-2" : "p-6"
        )}>
          <Link to="/" className={cn(
            "hidden md:flex items-center gap-3 mb-10 font-bold text-2xl tracking-tight transition-all shrink-0",
            isSidebarCollapsed ? "justify-center mt-4" : ""
          )}>
            <div className="w-10 h-10 bg-accent rounded-xl flex items-center justify-center text-white text-xl shrink-0">Q</div>
            {!isSidebarCollapsed && <span className="dark:text-white">QuicklyUtils<span className="text-accent">.</span></span>}
          </Link>

          <nav className={cn(
            "flex-1 space-y-8 overflow-y-auto overflow-x-hidden min-h-0 custom-scrollbar relative",
            isSidebarCollapsed ? "" : "pr-2"
          )}>
            {/* Favorites Category */}
            {currentUser && (
              <div className="mb-4">
                {!isSidebarCollapsed && (
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-600 mb-4 flex items-center gap-2 transition-all">
                    <LayoutGrid className="w-3 h-3" /> Workspace
                  </span>
                )}
                <ul className="space-y-1">
                  <li>
                    <SidebarTooltip tooltip="My Personal Panel" isCollapsed={isSidebarCollapsed}>
                      <Link 
                        to="/my-panel"
                        onClick={() => setIsMenuOpen(false)}
                        className={cn(
                          "flex items-center py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-200 group",
                          location.pathname === '/my-panel' 
                            ? "bg-accent/10 text-accent font-bold" 
                            : "text-slate-500 dark:text-slate-400 hover:text-accent hover:bg-slate-50 dark:hover:bg-slate-800",
                          isSidebarCollapsed ? "justify-center px-0 w-10 h-10 mx-auto" : "gap-3"
                        )}
                      >
                        <ShieldCheck className="w-4 h-4 opacity-70 group-hover:opacity-100 shrink-0" />
                        {!isSidebarCollapsed && "My Personal Panel"}
                      </Link>
                    </SidebarTooltip>
                  </li>
                  {currentUser.email === 'allysonbelo@gmail.com' && (
                    <li>
                      <SidebarTooltip tooltip="Admin Console" isCollapsed={isSidebarCollapsed}>
                        <Link 
                          to="/admin"
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            "flex items-center py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-200 group border border-dashed border-indigo-500/30 bg-indigo-50/10",
                            location.pathname === '/admin' 
                              ? "bg-indigo-500/20 text-indigo-500 font-bold" 
                              : "text-indigo-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20",
                            isSidebarCollapsed ? "justify-center px-0 w-10 h-10 mx-auto border-none" : "gap-3"
                          )}
                        >
                          <Settings className="w-4 h-4 opacity-70 group-hover:opacity-100 shrink-0" />
                          {!isSidebarCollapsed && "Admin Console"}
                        </Link>
                      </SidebarTooltip>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {favorites.length > 0 && (
              <div className="mb-4">
                {!isSidebarCollapsed && (
                  <span className="text-[11px] uppercase tracking-widest font-bold text-accent mb-4 flex items-center gap-2 transition-all">
                    <StarIcon className="w-3 h-3 fill-accent" /> Favorites
                  </span>
                )}
                <ul className="space-y-1">
                  {TOOLS.filter(t => favorites.includes(t.id)).map(tool => (
                    <li key={`fav-${tool.id}`}>
                      <SidebarTooltip tooltip={tool.name} isCollapsed={isSidebarCollapsed}>
                        <Link 
                          to={tool.path}
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            "flex items-center py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-200 group",
                            location.pathname === tool.path 
                              ? "bg-accent/10 text-accent font-bold" 
                              : "text-slate-500 dark:text-slate-400 hover:text-accent hover:bg-slate-50 dark:hover:bg-slate-800",
                            isSidebarCollapsed ? "justify-center px-0 w-10 h-10 mx-auto" : "gap-3"
                          )}
                        >
                          <tool.icon className="w-4 h-4 opacity-70 group-hover:opacity-100 shrink-0" />
                          {!isSidebarCollapsed && tool.name}
                        </Link>
                      </SidebarTooltip>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {CATEGORIES.map((cat) => (
              <div key={cat.id} className="mb-4">
                {!isSidebarCollapsed && (
                  <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-600 mb-4 flex items-center transition-all">
                    {cat.name} Utils
                  </span>
                )}
                <ul className="space-y-1">
                  {TOOLS.filter(t => t.category === cat.id).map(tool => (
                    <li key={tool.id}>
                      <SidebarTooltip tooltip={tool.name} isCollapsed={isSidebarCollapsed}>
                        <Link 
                          to={tool.path}
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            "flex items-center py-2.5 px-4 rounded-xl text-sm font-medium transition-all duration-200 group",
                            location.pathname === tool.path 
                              ? "bg-slate-50 dark:bg-slate-800 text-accent font-bold" 
                              : "text-slate-500 dark:text-slate-400 hover:text-accent hover:bg-slate-50 dark:hover:bg-slate-800",
                            isSidebarCollapsed ? "justify-center px-0 w-10 h-10 mx-auto" : "gap-3"
                          )}
                        >
                          <tool.icon className={cn(
                            "w-4 h-4 transition-colors shrink-0",
                            location.pathname === tool.path ? "text-accent" : "opacity-40 group-hover:opacity-100"
                          )} />
                          {!isSidebarCollapsed && tool.name}
                        </Link>
                      </SidebarTooltip>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="mt-auto pt-4 md:pt-8 border-t border-line dark:border-line-dark flex flex-col gap-4 shrink-0">
            {deferredPrompt && (
              <SidebarTooltip tooltip="Install App" isCollapsed={isSidebarCollapsed}>
                <button 
                  onClick={handleInstallClick}
                  className={cn(
                    "flex items-center justify-center bg-accent text-white rounded-2xl hover:bg-accent/90 transition-all shadow-lg shadow-accent/20 font-bold text-sm mx-auto w-full",
                    isSidebarCollapsed ? "w-12 h-12 p-0" : "gap-2 py-3 px-4"
                  )}
                >
                  <Download className="w-4 h-4 shrink-0" />
                  {!isSidebarCollapsed && "Install App"}
                </button>
              </SidebarTooltip>
            )}
            
            {/* User Session Info */}
            <SidebarTooltip tooltip="Account & Settings" isCollapsed={isSidebarCollapsed}>
              <button 
                onClick={() => setIsSettingsOpen(true)}
                className={cn(
                  "flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-line dark:border-slate-800 hover:border-accent transition-all group overflow-hidden",
                  isSidebarCollapsed ? "w-12 h-12 justify-center p-0 mx-auto" : "gap-3 p-3 w-full"
                )}
              >
                {currentUser ? (
                  <img 
                    src={currentUser.photoURL || ''} 
                    alt="" 
                    className={cn(
                      "rounded-full border border-line dark:border-slate-700 shrink-0 object-cover",
                      isSidebarCollapsed ? "w-10 h-10" : "w-8 h-8"
                    )} 
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className={cn(
                    "bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center shrink-0",
                    isSidebarCollapsed ? "w-10 h-10" : "w-8 h-8"
                  )}>
                    <ShieldCheck className="w-4 h-4 text-slate-400" />
                  </div>
                )}
                {!isSidebarCollapsed && (
                  <div className="flex-1 text-left overflow-hidden">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Account & Settings</p>
                    <p className="text-xs font-bold truncate dark:text-white">
                      {currentUser ? currentUser.displayName : 'Sign in to sync'}
                    </p>
                  </div>
                )}
                {!isSidebarCollapsed && <Settings className="w-4 h-4 text-slate-300 group-hover:text-accent transition-colors shrink-0" />}
              </button>
            </SidebarTooltip>

            <SidebarTooltip tooltip={darkMode ? "Light Mode" : "Dark Mode"} isCollapsed={isSidebarCollapsed}>
              <button 
                onClick={() => setDarkMode(!darkMode)}
                className={cn(
                  "flex items-center text-sm font-bold text-slate-400 hover:text-accent transition-colors group px-2",
                  isSidebarCollapsed ? "justify-center" : "gap-3"
                )}
              >
                {darkMode ? (
                  <>
                    <Sun className="w-4 h-4 group-hover:rotate-45 transition-transform" /> {!isSidebarCollapsed && "Light Mode"}
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 group-hover:-rotate-12 transition-transform" /> {!isSidebarCollapsed && "Dark Mode"}
                  </>
                )}
              </button>
            </SidebarTooltip>

            {!isSidebarCollapsed && (
              <div className="flex flex-col gap-2 px-2 mt-2">
                <div className="flex gap-4">
                  <Link to="/about" onClick={() => setIsMenuOpen(false)} className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-accent transition-colors">About Us</Link>
                  <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-accent transition-colors">Contact</Link>
                </div>
                <div className="flex gap-4">
                  <Link to="/privacy" onClick={() => setIsMenuOpen(false)} className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-accent transition-colors">Privacy</Link>
                  <Link to="/terms" onClick={() => setIsMenuOpen(false)} className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-accent transition-colors">Terms</Link>
                </div>
              </div>
            )}

            {!isSidebarCollapsed && (
              <div className="flex flex-col gap-2 px-2 mt-4 pt-4 border-t border-line dark:border-slate-800/50">
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  <span>© 2026 QuicklyUtils</span>
                  <div className="flex gap-4">
                    <a href="https://github.com/allysonbelo" target="_blank" rel="noreferrer" aria-label="GitHub Profile">
                      <Github className="w-4 h-4 cursor-pointer hover:text-slate-900 dark:hover:text-white transition-colors" />
                    </a>
                  </div>
                </div>
                <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400 mt-1">
                  Developed by <a href="https://allysonbelo.com" target="_blank" rel="noreferrer" className="text-accent hover:underline font-bold uppercase tracking-widest">Allyson Belo</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
      </TooltipProvider>

      {/* Main Content */}
      <main className={cn(
        "flex-1 overflow-y-auto transition-all duration-300 pb-[120px] md:pb-0",
        isSidebarCollapsed ? "md:ml-0" : ""
      )}>
        <div className={cn(
          "h-full transition-all duration-300",
          isSidebarCollapsed ? "md:pl-20" : "md:pl-80"
        )}>
          <AnimatePresence mode="wait">
          {/* @ts-ignore */}
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Dashboard favorites={favorites} toggleFavorite={toggleFavorite} setToast={setToast} />} />
            <Route path="/my-panel" element={<UserPanel currentUser={currentUser} favorites={favorites} toggleFavorite={toggleFavorite} history={history} />} />
            <Route path="/admin" element={<AdminPanel userEmail="allysonbelo@gmail.com" setToast={setToast} />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact setToast={setToast} />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {TOOLS.map(tool => {
              const RouteComp = Route as any;
              return (
                <RouteComp 
                  key={tool.id} 
                  path={tool.path} 
                  element={
                    <ToolLayout 
                      tool={tool} 
                      isFavorite={favorites.includes(tool.id)} 
                      onToggleFavorite={() => toggleFavorite(tool.id)}
                      onShare={handleShare}
                      onHistory={() => setIsHistoryOpen(true)}
                    >
                      <React.Suspense fallback={
                        <div className="flex flex-col items-center justify-center p-12 w-full h-full text-slate-400">
                          <div className="w-8 h-8 border-4 border-slate-200 dark:border-slate-700 border-t-accent rounded-full animate-spin"></div>
                        </div>
                      }>
                        {TOOL_COMPONENTS[tool.id] || <PlaceholderTool name={tool.name} />}
                      </React.Suspense>
                    </ToolLayout>
                  } 
                />
              );
            })}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </div>
    </main>

    {/* Mobile Bottom Navigation */}
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[1000] bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-t border-line dark:border-slate-800 flex items-center justify-around pb-safe pt-2 px-2 shadow-[0_-10px_40px_rgba(0,0,0,0.05)]">
      <Link 
        to="/" 
        onClick={() => {
          setIsMenuOpen(false);
          setIsHistoryOpen(false);
          setIsSettingsOpen(false);
        }}
        className={cn(
          "flex flex-col items-center justify-center p-2 rounded-xl min-w-[64px] transition-all duration-300 relative",
          isHomeActive ? "text-accent font-bold" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
        )}
      >
        <motion.div
          animate={{
            y: isHomeActive ? -30 : 0,
            scale: isHomeActive ? 1 : 0.3,
            opacity: isHomeActive ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 25, mass: 0.8 }}
          className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white shadow-lg shadow-accent/30 border-4 border-white dark:border-bg-dark z-10 pointer-events-none"
        >
          <LayoutGrid className="w-5 h-5" />
        </motion.div>
        
        <motion.div
          animate={{
            y: isHomeActive ? 20 : 0,
            opacity: isHomeActive ? 0 : 1,
            scale: isHomeActive ? 0.5 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <LayoutGrid className="w-5 h-5 mb-1" />
        </motion.div>
        <span className={cn("text-[10px] font-medium transition-all duration-300 absolute bottom-1", isHomeActive ? "opacity-100 translate-y-0 text-accent" : "opacity-0 translate-y-4")}>Home</span>
        <span className={cn("text-[10px] font-medium transition-all duration-200", isHomeActive ? "opacity-0" : "opacity-100")}>Home</span>
      </Link>

      {currentUser && (
        <Link 
          to="/my-panel" 
          onClick={() => {
            setIsMenuOpen(false);
            setIsHistoryOpen(false);
            setIsSettingsOpen(false);
          }}
          className={cn(
            "flex flex-col items-center justify-center p-2 rounded-xl min-w-[64px] transition-all duration-300 relative",
            isMyPanelActive ? "text-accent font-bold" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
          )}
        >
          <motion.div
            animate={{
              y: isMyPanelActive ? -30 : 0,
              scale: isMyPanelActive ? 1 : 0.3,
              opacity: isMyPanelActive ? 1 : 0,
            }}
            transition={{ type: "spring", stiffness: 400, damping: 25, mass: 0.8 }}
            className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white shadow-lg shadow-accent/30 border-4 border-white dark:border-bg-dark z-10 pointer-events-none"
          >
            <ShieldCheck className="w-5 h-5" />
          </motion.div>
          
          <motion.div
            animate={{
              y: isMyPanelActive ? 20 : 0,
              opacity: isMyPanelActive ? 0 : 1,
              scale: isMyPanelActive ? 0.5 : 1,
            }}
            transition={{ duration: 0.2 }}
          >
            <ShieldCheck className="w-5 h-5 mb-1" />
          </motion.div>
          <span className={cn("text-[10px] font-medium transition-all duration-300 absolute bottom-1", isMyPanelActive ? "opacity-100 translate-y-0 text-accent" : "opacity-0 translate-y-4")}>My Panel</span>
          <span className={cn("text-[10px] font-medium transition-all duration-200", isMyPanelActive ? "opacity-0" : "opacity-100")}>My Panel</span>
        </Link>
      )}

      <button 
        onClick={() => { 
          setIsMenuOpen(!isMenuOpen);
          if (!isMenuOpen) {
            setIsHistoryOpen(false);
            setIsSettingsOpen(false);
          }
        }}
        className={cn(
          "flex flex-col items-center justify-center p-2 rounded-xl min-w-[64px] transition-all duration-300 relative",
          isMenuOpen ? "text-accent font-bold" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
        )}
      >
        <motion.div
          animate={{
            y: isMenuOpen ? -30 : 0,
            scale: isMenuOpen ? 1 : 0.3,
            opacity: isMenuOpen ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 25, mass: 0.8 }}
          className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white shadow-lg shadow-accent/30 border-4 border-white dark:border-bg-dark z-10 pointer-events-none"
        >
          {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </motion.div>
        
        <motion.div
          animate={{
            y: isMenuOpen ? 20 : 0,
            opacity: isMenuOpen ? 0 : 1,
            scale: isMenuOpen ? 0.5 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <Menu className="w-5 h-5 mb-1" />
        </motion.div>
        <span className={cn("text-[10px] font-medium transition-all duration-300 absolute bottom-1", isMenuOpen ? "opacity-100 translate-y-0 text-accent" : "opacity-0 translate-y-4")}>Tools</span>
        <span className={cn("text-[10px] font-medium transition-all duration-200", isMenuOpen ? "opacity-0" : "opacity-100")}>Tools</span>
      </button>

      <button 
        onClick={() => { 
          setIsHistoryOpen(!isHistoryOpen);
          if (!isHistoryOpen) {
            setIsMenuOpen(false); 
            setIsSettingsOpen(false);
          }
        }}
        className={cn(
          "flex flex-col items-center justify-center p-2 rounded-xl min-w-[64px] transition-all duration-300 relative",
          isHistoryOpen ? "text-accent font-bold" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
        )}
      >
        <motion.div
          animate={{
            y: isHistoryOpen ? -30 : 0,
            scale: isHistoryOpen ? 1 : 0.3,
            opacity: isHistoryOpen ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 25, mass: 0.8 }}
          className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white shadow-lg shadow-accent/30 border-4 border-white dark:border-bg-dark z-10 pointer-events-none"
        >
          <HistoryIcon className="w-5 h-5" />
        </motion.div>
        
        <motion.div
          animate={{
            y: isHistoryOpen ? 20 : 0,
            opacity: isHistoryOpen ? 0 : 1,
            scale: isHistoryOpen ? 0.5 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <HistoryIcon className="w-5 h-5 mb-1" />
        </motion.div>
        <span className={cn("text-[10px] font-medium transition-all duration-300 absolute bottom-1", isHistoryOpen ? "opacity-100 translate-y-0 text-accent" : "opacity-0 translate-y-4")}>History</span>
        <span className={cn("text-[10px] font-medium transition-all duration-200", isHistoryOpen ? "opacity-0" : "opacity-100")}>History</span>
      </button>

      <button 
        onClick={() => { 
          setIsSettingsOpen(!isSettingsOpen);
          if (!isSettingsOpen) {
            setIsMenuOpen(false); 
            setIsHistoryOpen(false);
          }
        }}
        className={cn(
          "flex flex-col items-center justify-center p-2 rounded-xl min-w-[64px] transition-all duration-300 relative",
          isSettingsOpen ? "text-accent font-bold" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
        )}
      >
        <motion.div
          animate={{
            y: isSettingsOpen ? -30 : 0,
            scale: isSettingsOpen ? 1 : 0.3,
            opacity: isSettingsOpen ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 400, damping: 25, mass: 0.8 }}
          className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white shadow-lg shadow-accent/30 border-4 border-white dark:border-bg-dark z-10 pointer-events-none"
        >
          <Settings className={cn("w-5 h-5", isSettingsOpen && "animate-spin-slow")} />
        </motion.div>
        
        <motion.div
          animate={{
            y: isSettingsOpen ? 20 : 0,
            opacity: isSettingsOpen ? 0 : 1,
            scale: isSettingsOpen ? 0.5 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          <Settings className="w-5 h-5 mb-1" />
        </motion.div>
        <span className={cn("text-[10px] font-medium transition-all duration-300 absolute bottom-1", isSettingsOpen ? "opacity-100 translate-y-0 text-accent" : "opacity-0 translate-y-4")}>Settings</span>
        <span className={cn("text-[10px] font-medium transition-all duration-200", isSettingsOpen ? "opacity-0" : "opacity-100")}>Settings</span>
      </button>
    </nav>
    <CookieBanner />
      
      {/* Global User Side Panel */}
      {currentUser && (
        <>
          <AnimatePresence>
            {isUserPanelOpen && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsUserPanelOpen(false)}
                className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[105]"
              />
            )}
          </AnimatePresence>
          <UserSidePanel 
            user={currentUser} 
            isOpen={isUserPanelOpen} 
            onClose={() => setIsUserPanelOpen(false)} 
          />
        </>
      )}

      {/* Global Admin Quick Access Support */}
      {isAdmin && (
        <motion.div 
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[120] p-4 flex justify-center pointer-events-none"
        >
          <div className="bg-slate-900 dark:bg-slate-900 border border-white/10 rounded-2xl py-3 px-6 flex items-center gap-6 shadow-2xl pointer-events-auto ring-4 ring-black/5 backdrop-blur-xl">
            <div className="flex items-center gap-2 pr-6 border-r border-white/10">
              <span className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Admin Console Access</span>
            </div>
            <div className="flex items-center gap-6">
              <Link 
                to="/admin" 
                className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-accent transition-colors flex items-center gap-2"
              >
                <Settings className="w-3.5 h-3.5" /> Dashboard
              </Link>
              <Link 
                to="/contact" 
                className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-emerald-400 transition-colors flex items-center gap-2"
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Support Messages
              </Link>
              <Link 
                to="/my-panel" 
                className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-amber-400 transition-colors flex items-center gap-2"
              >
                <UserIcon className="w-3.5 h-3.5" /> Profile Settings
              </Link>
            </div>
          </div>
        </motion.div>
      )}

      {/* Settings Modal */}
      <AnimatePresence>
        {isSettingsOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-[120px] md:pb-4 pointer-events-none">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSettingsOpen(false)}
              className="absolute inset-0 bg-ink/40 backdrop-blur-sm pointer-events-auto"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-2xl border border-line dark:border-slate-800 flex flex-col pointer-events-auto max-h-full"
            >
              <div className="flex items-center justify-between mb-8 shrink-0">
                <h3 className="text-xl font-bold">App Settings</h3>
                <button 
                  onClick={() => setIsSettingsOpen(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6 overflow-y-auto custom-scrollbar pr-2 min-h-0">
                <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-line dark:border-slate-800">
                  <h4 className="text-sm font-bold mb-1">Theme Preferences</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Choose how QuicklyUtils looks for you.</p>
                  <button 
                    onClick={() => setDarkMode(!darkMode)}
                    className="w-full py-3 bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-xl text-sm font-bold flex items-center justify-center gap-3 hover:border-accent transition-all"
                  >
                    {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    Switch to {darkMode ? 'Light' : 'Dark'} Mode
                  </button>
                </div>

                <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-line dark:border-slate-800">
                  <h4 className="text-sm font-bold mb-1">Account</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Sign in to sync your tools and access admin features.</p>
                  
                  {currentUser ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3 bg-white dark:bg-slate-900 p-3 rounded-xl border border-line dark:border-slate-800">
                        <img 
                          src={currentUser.photoURL || ''} 
                          alt={currentUser.displayName || ''} 
                          className="w-8 h-8 rounded-full border border-line dark:border-slate-700" 
                          referrerPolicy="no-referrer"
                        />
                        <div className="overflow-hidden">
                          <p className="font-bold text-xs truncate">{currentUser.displayName}</p>
                          <p className="text-[10px] text-slate-500 truncate">{currentUser.email}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Link 
                          to="/my-panel"
                          onClick={() => setIsSettingsOpen(false)}
                          className="py-2 bg-accent/10 text-accent rounded-xl font-bold text-xs hover:bg-accent/20 transition-colors flex items-center justify-center gap-2"
                        >
                          <UserIcon className="w-3 h-3" /> Manage Profile
                        </Link>
                        <button 
                          onClick={() => {
                            authService.signOut();
                            setIsSettingsOpen(false);
                          }}
                          className="py-2 bg-rose-50 dark:bg-rose-900/10 text-rose-500 rounded-xl font-bold text-xs hover:bg-rose-100 transition-colors"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button 
                      onClick={() => {
                        authService.signIn();
                        setIsSettingsOpen(false);
                      }}
                      className="w-full py-3 bg-accent text-white rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-accent/20"
                    >
                      Sign in with Google
                    </button>
                  )}
                </div>

                <div className="p-6 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-line dark:border-slate-800">
                  <h4 className="text-sm font-bold mb-1">Privacy Preferences</h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Manage your cookie consent and privacy settings.</p>
                  <button 
                    onClick={() => {
                      localStorage.removeItem('cookie-consent');
                      window.location.reload();
                    }}
                    className="w-full py-3 bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-xl text-sm font-bold flex items-center justify-center gap-3 hover:border-accent transition-all"
                  >
                    Reset Cookie Consent
                  </button>
                </div>

                <div className="p-6 bg-rose-50/50 dark:bg-rose-950/10 rounded-2xl border border-rose-100 dark:border-rose-900/30">
                  <h4 className="text-sm font-bold text-rose-600 mb-1">Danger Zone</h4>
                  <p className="text-xs text-rose-500/70 mb-4">Irreversible actions for your local data.</p>
                  <button 
                    onClick={() => {
                      setConfirmDialog({
                        message: 'Clear all favorites?',
                        subMessage: 'This will remove all tools from your favorites list. This action cannot be undone.',
                        onConfirm: () => {
                          setFavorites([]);
                          setIsSettingsOpen(false);
                          setConfirmDialog(null);
                        }
                      });
                    }}
                    className="w-full py-3 bg-rose-500 text-white rounded-xl text-sm font-bold hover:bg-rose-600 transition-all shadow-lg shadow-rose-500/20"
                  >
                    Clear All Favorites
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-line dark:border-slate-800 space-y-1">
                   <p className="text-[10px] font-bold text-slate-300 dark:text-slate-700 uppercase tracking-[0.2em]">QuicklyUtils v1.0.4 • 2026</p>
                   <p className="text-[10px] font-bold text-slate-300 dark:text-slate-700 uppercase tracking-widest">
                     Developed by <a href="https://allysonbelo.com" target="_blank" rel="noreferrer" className="text-accent hover:underline">Allyson Belo</a>
                   </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirm Modal */}
      <AnimatePresence>
        {confirmDialog && (
          <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 pb-[120px] md:pb-4 pointer-events-none">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmDialog(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl p-6 shadow-2xl shadow-rose-900/20 border border-line dark:border-slate-800 pointer-events-auto"
            >
              <div className="w-12 h-12 bg-rose-100 dark:bg-rose-900/30 text-rose-500 rounded-2xl flex items-center justify-center mb-6">
                <Trash2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold tracking-tight mb-2">{confirmDialog.message}</h3>
              {confirmDialog.subMessage && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">{confirmDialog.subMessage}</p>
              )}
              <div className="flex gap-3">
                <button 
                  onClick={() => setConfirmDialog(null)}
                  className="flex-1 py-3 px-4 rounded-xl font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    confirmDialog.onConfirm();
                    setConfirmDialog(null);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl font-bold bg-rose-500 text-white hover:bg-rose-600 transition-colors shadow-lg shadow-rose-500/20"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* History Modal */}
      <AnimatePresence>
        {isHistoryOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-[120px] md:pb-4 pointer-events-none">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsHistoryOpen(false)}
              className="absolute inset-0 bg-ink/40 backdrop-blur-sm pointer-events-auto"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-2xl border border-line dark:border-slate-800 flex flex-col pointer-events-auto max-h-full"
            >
              <div className="flex items-center justify-between mb-6 shrink-0">
                <div className="flex items-center gap-3 text-accent">
                  <HistoryIcon className="w-5 h-5" />
                  <h3 className="text-xl font-bold">Recent History</h3>
                </div>
                <button 
                  onClick={() => setIsHistoryOpen(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar min-h-0">
                {history.length > 0 ? (
                  history.map((id: string) => {
                    const tool = TOOLS.find(t => t.id === id);
                    if (!tool) return null;
                    return (
                      <Link
                        key={`history-${id}`}
                        to={tool.path}
                        onClick={() => setIsHistoryOpen(false)}
                        className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 hover:border-accent transition-all group"
                      >
                        <div className="w-10 h-10 bg-white dark:bg-slate-900 rounded-xl flex items-center justify-center text-slate-400 group-hover:text-accent border border-line dark:border-slate-800 transition-colors">
                          <tool.icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold truncate transition-colors">{tool.name}</h4>
                          <p className="text-[10px] text-slate-500 truncate uppercase tracking-wider">{tool.category} utility</p>
                        </div>
                        <Share2 className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-all" />
                      </Link>
                    );
                  })
                ) : (
                  <div className="text-center py-12">
                    <HistoryIcon className="w-12 h-12 text-slate-200 dark:text-slate-800 mx-auto mb-4" />
                    <p className="text-slate-400 text-sm">No activity recorded yet.</p>
                  </div>
                )}
              </div>

              <div className="mt-8 pt-6 border-t border-line dark:border-slate-800 shrink-0">
                <button 
                  onClick={() => {
                    setConfirmDialog({
                      message: 'Clear all history?',
                      subMessage: 'This will permanently remove your activity history on this device.',
                      onConfirm: () => {
                        setHistory([]);
                        setIsHistoryOpen(false);
                      }
                    });
                  }}
                  className="w-full py-3 text-slate-400 hover:text-rose-500 text-[10px] font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                >
                  Clear history data
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}


